import React from 'react';

const OrganizationDetails = ({ organization }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      if (typeof dateString === 'object' && dateString.seconds) {
        const d = new Date(dateString.seconds * 1000);
        return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
      }
      if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
        return dateString;
      }
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
      }
      return 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  const formatLocation = () => {
    const city = organization.city || '';
    const state = organization.state || '';
    const country = organization.country || '';
    const parts = [city, state, country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const statusRaw = organization.status;
  const isActive = statusRaw && typeof statusRaw === 'string' && statusRaw.toLowerCase() === 'active';
  const statusLabel = statusRaw && typeof statusRaw === 'string' ? statusRaw.toUpperCase() : 'N/A';

  const statusColors = {
    active: { bg: '#16a34a', text: '#fff' },
    expired: { bg: '#dc2626', text: '#fff' },
    suspended: { bg: '#d97706', text: '#fff' },
  };
  const statusColor = statusColors[statusRaw?.toLowerCase()] || { bg: '#16a34a', text: '#fff' };

  const labelStyle = { fontSize: '11px', fontWeight: '700', color: '#8a95a3', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '4px' };
  const valueStyle = { fontSize: '14px', color: '#0a1628', fontWeight: '500' };

  return (
    <div style={{ marginTop: '28px' }}>
      {/* Result Header Bar */}
      <div style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #0d2347 100%)',
        borderRadius: '10px', padding: '20px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '12px', marginBottom: '24px',
      }}>
        <div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '6px' }}>
            Verified Organization
          </div>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff', margin: 0, lineHeight: 1.4 }}>
            {organization.org_name || 'N/A'}
          </h2>
        </div>
        {statusRaw && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            backgroundColor: statusColor.bg, color: statusColor.text,
            borderRadius: '20px', padding: '6px 14px', fontSize: '12px',
            fontWeight: '700', letterSpacing: '0.6px', whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}>
            {isActive && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
            {statusLabel}
          </span>
        )}
      </div>

      {/* Details Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 32px', marginBottom: '20px' }}>
        <div>
          <div style={labelStyle}>Certificate No.</div>
          <div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: '13.5px', color: '#0d2347', fontWeight: '600' }}>
            {organization.certi_no || 'N/A'}
          </div>
        </div>
        <div>
          <div style={labelStyle}>Name of Organization</div>
          <div style={valueStyle}>{organization.org_name || 'N/A'}</div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={labelStyle}>Address of Organization</div>
          <div style={valueStyle}>{formatLocation()}</div>
        </div>
      </div>

      {/* Gold divider */}
      <div style={{ height: '2px', background: 'linear-gradient(90deg, #c9a227, #e8c547, #c9a227)', borderRadius: '2px', marginBottom: '20px', opacity: 0.6 }} />

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px 24px', marginBottom: statusRaw ? '20px' : '0' }}>
        <div style={{ background: '#f7f9fc', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Date of Issue</div>
          <div style={valueStyle}>{formatDate(organization.issue_date)}</div>
        </div>
        <div style={{ background: '#f7f9fc', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Expiry Date</div>
          <div style={valueStyle}>{formatDate(organization.exp_date)}</div>
        </div>
        <div style={{ background: '#f7f9fc', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Status</div>
          <div style={{ ...valueStyle, color: statusColor.bg, fontWeight: '700' }}>{statusLabel}</div>
        </div>
        <div style={{ background: '#f7f9fc', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Certificate Standard</div>
          <div style={valueStyle}>{organization.program || 'N/A'}</div>
        </div>
      </div>

      {organization.notes && (
        <div style={{ background: '#f7f9fc', borderRadius: '8px', padding: '14px 16px', marginTop: '16px' }}>
          <div style={labelStyle}>Certification Scope</div>
          <div style={valueStyle}>{organization.notes}</div>
        </div>
      )}
    </div>
  );
};

export default OrganizationDetails;
